import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const InteractiveDemo = () => {
  const [currentStep, setCurrentStep] = useState(0);

  const demoSteps = [
    {
      title: "Step 1: Receive Your Scenario",
      description: "Each day, you'll get a realistic sales scenario tailored to your industry.",
      example: `"A prospect says: 'Your price is too high compared to competitors.' How would you respond?"`,
      icon: "MessageSquare",
      color: "var(--color-primary)"
    },
    {
      title: "Step 2: Craft Your Response",
      description: "Type out how you would handle the situation. Be natural and authentic.",
      example: "I understand price is important. Let me show you the ROI our clients typically see in the first 90 days...",
      icon: "Edit3",
      color: "var(--color-accent)"
    },
    {
      title: "Step 3: Get AI Feedback",
      description: "Lumo analyzes your response across four key dimensions with actionable insights.",
      example: null,
      icon: "BarChart3",
      color: "var(--color-secondary)",
      scores: [
        { label: "Clarity", value: 85, color: "var(--color-primary)" },
        { label: "Confidence", value: 78, color: "var(--color-accent)" },
        { label: "Control", value: 82, color: "var(--color-secondary)" },
        { label: "Close", value: 90, color: "var(--color-success)" }
      ],
      feedback: [
        "✓ Strong value proposition with ROI focus",
        "✓ Confident tone without being defensive",
        "→ Consider asking a question to understand their budget constraints",
        "→ Add a specific success story to strengthen credibility"
      ]
    },
    {
      title: "Step 4: Learn & Improve",
      description: "See an improved version of your response and understand what makes it effective.",
      example: `"I appreciate you being upfront about price. Many of our clients had the same concern initially. Can I ask - what's driving your budget constraints right now? Understanding that helps me show you exactly how we deliver 3x ROI in 90 days, like we did for [Company Name] in your industry."`,
      icon: "Lightbulb",
      color: "var(--color-success)"
    }
  ];

  const currentDemo = demoSteps?.[currentStep];

  const handleNext = () => {
    if (currentStep < demoSteps?.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="mb-12 px-4">
      <h2 className="text-2xl font-bold text-foreground text-center mb-8">
        See How It Works
      </h2>
      <div className="max-w-3xl mx-auto">
        <div className="bg-card rounded-2xl border border-border overflow-hidden shadow-card">
          <div className="bg-gradient-to-r from-primary/10 to-accent/10 p-6 border-b border-border">
            <div className="flex items-center gap-4 mb-4">
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: currentDemo?.color }}
              >
                <Icon name={currentDemo?.icon} size={28} color="#FFFFFF" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-foreground">
                  {currentDemo?.title}
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {currentDemo?.description}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {demoSteps?.map((_, index) => (
                <div
                  key={index}
                  className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                    index <= currentStep ? 'bg-primary' : 'bg-muted'
                  }`}
                />
              ))}
            </div>
          </div>

          <div className="p-6">
            {currentDemo?.example && (
              <div className="bg-muted/50 rounded-xl p-4 mb-4">
                <p className="text-sm text-foreground leading-relaxed whitespace-pre-line">
                  {currentDemo?.example}
                </p>
              </div>
            )}

            {currentDemo?.scores && (
              <div className="space-y-4 mb-4">
                {currentDemo?.scores?.map((score, index) => (
                  <div key={index}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-foreground">
                        {score?.label}
                      </span>
                      <span className="text-sm font-bold text-foreground">
                        {score?.value}/100
                      </span>
                    </div>
                    <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${score?.value}%`,
                          backgroundColor: score?.color
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {currentDemo?.feedback && (
              <div className="bg-muted/50 rounded-xl p-4 space-y-2">
                {currentDemo?.feedback?.map((item, index) => (
                  <p key={index} className="text-sm text-foreground leading-relaxed">
                    {item}
                  </p>
                ))}
              </div>
            )}
          </div>

          <div className="p-6 border-t border-border flex items-center justify-between">
            <Button
              variant="outline"
              onClick={handlePrev}
              disabled={currentStep === 0}
              iconName="ChevronLeft"
              iconPosition="left"
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              {currentStep + 1} of {demoSteps?.length}
            </span>
            <Button
              variant="default"
              onClick={handleNext}
              disabled={currentStep === demoSteps?.length - 1}
              iconName="ChevronRight"
              iconPosition="right"
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractiveDemo;